import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { MintTile } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type ArtifactType } from '@app/client/common/structs/nft/artifacts';
import { generateDataItem, getTranslationByArtifactKey } from '@app/helpers';
import { setSelectedArtifactMintDisplay } from '@app/store/actions/artifacts';
import { artifactDisplayMetadata } from '@app/store/selectors/artifacts/artifacts-metadata';
import {
    artifactMintDisplaySelector,
    artifactMintSelector,
    selectedArtifactIdSelector,
} from '@app/store/selectors/artifacts/minting';

import { Typography } from '../../../DesignSystem';

import css from './ArtifactMintingList.module.css';

interface ArtifactMintingListProps {
    artifactType: ArtifactType;
}

export function ArtifactMintingList({ artifactType }: ArtifactMintingListProps) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(['artifacts']);

    const { artifactsBreakdown } = useAppSelector((state) => state.artifacts);
    const artifactTypeDisplayItems = useAppSelector(artifactDisplayMetadata);
    const artifactMintDetails = useAppSelector(artifactMintSelector);
    const mintDisplayItem = useAppSelector(artifactMintDisplaySelector);
    const selectedArtifactId = useAppSelector(selectedArtifactIdSelector);
    const isArtifactMintPanelOpen = useAppSelector((state) => state.artifacts.artifactMintPanelOpen);

    const breakdown = artifactsBreakdown?.[artifactType];
    const tokenId = mintDisplayItem[artifactType]?.token_id;
    const minted = mintDisplayItem[artifactType]?.minted;

    const [items, setItems] = useState(breakdown?.eligible_for_minting);

    const handleSelect = useCallback(
        function (_selectedArtifactId, _selectedArtifactType) {
            if (!artifactsBreakdown) return;
            const item = generateDataItem().mintDisplayItem(
                artifactTypeDisplayItems[_selectedArtifactType],
                artifactMintDetails[_selectedArtifactType],
                artifactsBreakdown[_selectedArtifactType],
                _selectedArtifactId,
            );
            dispatch(setSelectedArtifactMintDisplay(item));
        },
        [dispatch, artifactsBreakdown, artifactTypeDisplayItems, artifactMintDetails],
    );

    useEffect(() => {
        if (artifactsBreakdown && breakdown?.eligible_for_minting && isArtifactMintPanelOpen) {
            const firstItemId = breakdown.eligible_for_minting[0];
            const item = generateDataItem().mintDisplayItem(
                artifactTypeDisplayItems[artifactType],
                artifactMintDetails[artifactType],
                artifactsBreakdown[artifactType],
                firstItemId,
            );
            setItems(breakdown.eligible_for_minting);
            dispatch(setSelectedArtifactMintDisplay(item));
        }
        // for some reason if artifactMintDetails or artifactTypeDisplayItems get listed as a dep, this goes into an infinite loop
        // TODO obviously we should fix this more reliably at some point
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artifactsBreakdown, breakdown, artifactType, isArtifactMintPanelOpen]);

    return (
        <div className={css.container}>
            <h1 className={css.title}>
                {t('select_artifact_title', { artifact: getTranslationByArtifactKey(t, artifactType) })}
            </h1>
            <Typography type="paragraph" size={16} classes={['artifact-sidebar__text']} weight="600">
                <span>{t('select_artifact_copy')}</span>
            </Typography>

            <div className={css.items}>
                {items?.map((id) => {
                    return (
                        <div className={css.item} key={id}>
                            <MintTile
                                artifactType={artifactType}
                                artifactId={id}
                                tokenId={tokenId || undefined}
                                onSelect={handleSelect}
                                selected={id === selectedArtifactId}
                                selectable={true}
                                minted={minted}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
